import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';

// The social feed posts our social media activity (our posts and replies, and
// replies to or mentions of us) to one Discord channel. Each platform polls
// only when it has credentials in the bot's env and is switched on here.
class SocialFeedView extends BaseComponent {
    static properties = {
        _feed: { type: Object, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _notice: { type: String, state: true },
        _channelID: { type: String, state: true },
        _saving: { type: Boolean, state: true },
        _switching: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
    `;

    constructor() {
        super();
        this._feed = null;
        this._loading = true;
        this._error = null;
        this._notice = null;
        this._channelID = '';
        this._saving = false;
        this._switching = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            this._setFeed(await api.get('/api/social-feed'));
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _setFeed(feed) {
        this._feed = feed;
        this._channelID = feed?.channel?.DiscordChannelID ?? '';
    }

    async _saveChannel(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        this._notice = null;
        try {
            await api.put('/api/social-feed/channel', {
                discord_channel_id: this._channelID.trim(),
            });
            await this._load();
            this._notice = 'Saved.';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _clearChannel() {
        this._error = null;
        this._notice = null;
        try {
            await api.delete('/api/social-feed/channel');
            await this._load();
            this._notice = 'Cleared. Nothing will be posted until a channel is set.';
        } catch (err) {
            this._error = err.message;
        }
    }

    async _test() {
        this._error = null;
        this._notice = null;
        try {
            await api.post('/api/social-feed/test');
            this._notice = 'Test notification sent.';
        } catch (err) {
            this._error = err.message;
        }
    }

    async _setEnabled(platform, enabled) {
        this._switching = platform.id;
        this._error = null;
        this._notice = null;
        try {
            // api.put expects a JSON body back, and this answers 204 with none.
            const response = await api.fetch(`/api/social-feed/platforms/${platform.id}`, {
                method: 'PUT',
                body: JSON.stringify({ enabled }),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || `API error: ${response.status}`);
            }
            await this._load();
            this._notice = enabled
                ? `${platform.name} is on. Only activity from now on is posted.`
                : `${platform.name} is off.`;
        } catch (err) {
            this._error = err.message;
        } finally {
            this._switching = null;
        }
    }

    _status(platform) {
        if (!platform.configured) {
            return html`<span class="text-muted">No credentials</span>`;
        }
        if (!platform.enabled) {
            return html`<span class="text-muted">Off</span>`;
        }
        if (platform.last_error) {
            return html`<span class="text-danger" title="${platform.last_error}">Error: ${platform.last_error}</span>`;
        }
        if (platform.last_polled_at) {
            return html`<span class="text-success">Polled ${new Date(platform.last_polled_at).toLocaleString()}</span>`;
        }
        return html`<span class="text-muted">Waiting for the first poll</span>`;
    }

    render() {
        if (this._loading) {
            return html`<div class="text-center py-4"><span class="spinner-border"></span></div>`;
        }

        const hasChannel = Boolean(this._feed?.channel);
        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Social Media</h5>
                ${
                    hasChannel
                        ? html`
                    <div class="d-flex gap-2">
                        <button class="btn btn-outline-info btn-sm" title="Send test notification" @click="${this._test}">
                            <i class="bi bi-send"></i> Test
                        </button>
                        <button class="btn btn-outline-danger btn-sm" title="Clear the channel" @click="${this._clearChannel}">
                            <i class="bi bi-x-circle"></i> Clear
                        </button>
                    </div>
                `
                        : ''
                }
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                Our posts and replies, and replies to or mentions of us, are posted to this channel
                from every platform that is switched on below. A platform needs its credentials in
                the bot's env before it can be switched on.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._notice ? html`<div class="alert alert-success">${this._notice}</div>` : ''}

            <form @submit="${(e) => this._saveChannel(e)}" style="max-width: 500px;" class="mb-4">
                <div class="mb-3">
                    <label class="form-label">Discord Channel ID</label>
                    <input type="text" class="form-control" placeholder="e.g. 1234567890"
                           .value="${this._channelID}"
                           @input="${(e) => {
                               this._channelID = e.target.value;
                           }}"
                           required>
                    <div class="form-text">Enable Developer Mode in Discord settings, then right-click the channel and Copy Channel ID</div>
                </div>
                <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                    ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                </button>
            </form>

            <table class="table align-middle" style="max-width: 900px;">
                <thead>
                    <tr><th>Platform</th><th>On</th><th>Status</th></tr>
                </thead>
                <tbody>
                    ${(this._feed?.platforms ?? []).map(
                        (platform) => html`
                        <tr>
                            <td>${platform.name}</td>
                            <td>
                                <div class="form-check form-switch mb-0">
                                    <input class="form-check-input" type="checkbox" role="switch"
                                           .checked="${platform.enabled}"
                                           ?disabled="${(!platform.configured && !platform.enabled) || this._switching === platform.id}"
                                           @change="${(e) => this._setEnabled(platform, e.target.checked)}">
                                </div>
                            </td>
                            <td style="font-size: 0.875rem;">${this._status(platform)}</td>
                        </tr>
                    `,
                    )}
                </tbody>
            </table>
        `;
    }
}

customElements.define('social-feed-view', SocialFeedView);
